import React, { useEffect, useState } from 'react';

type HtmlBlobProps = {
  blok: {
    blob?: string;
    url?: string;
    iframe_height: number;
    css_injection: boolean;
    automatic_resize: boolean;
    scroll_hijack: boolean;
    min_height: boolean;
  };
  slot: string;
};

const iframeAutomaticResizeScript = `
  <script script type = "text/javascript">
    window.addEventListener('load', () => {
      window.setTimeout(() => window.frameElement.setAttribute('height', document.body.scrollHeight), 0)
    });
  </script>
`;

const iframeAutomaticCssInjection = `
  <style>header, footer, #onetrust-banner-sdk {display: none;}</style>
  <link rel="stylesheet" href="https://frontend.roche.com/frontend/releases/5.2.3/resources/css/fontsFallback.css">
`;

const getParsedBlob = (
  blob: string,
  iframeHeight: number,
  shouldAddCssInjection: boolean,
  shouldAutomaticResize: boolean,
): string => `
  ${blob}
  ${shouldAddCssInjection ? iframeAutomaticCssInjection : ''}
  ${shouldAutomaticResize && !iframeHeight ? iframeAutomaticResizeScript : ''}
`;

export const HtmlBlob = ({ blok, slot }: HtmlBlobProps): JSX.Element => {
  const [state, setState] = useState({ iframe_key: 0 });

  const iframeRefresh = () => {
    setState((prevState) => ({ iframe_key: prevState.iframe_key + 1 }));
  };

  const {
    blob,
    url,
    iframe_height: iframeHeight,
    css_injection: shouldAddCssInjection,
    automatic_resize: shouldAutomaticResize,
    scroll_hijack: shouldHijackScroll,
    min_height: shouldAddMinHeigt,
  } = blok;

  useEffect(() => {
    if (!shouldHijackScroll) {
      return;
    }
    document?.body.setAttribute('style', 'overflow: hidden');
  }, []);

  if (typeof window !== 'undefined') {
    window.addEventListener('onetrustInitialized', () => {
      [
        '#onetrust-accept-btn-handler',
        '#accept-recommended-btn-handler',
        '.ot-pc-refuse-all-handler',
        '#onetrust-reject-all-handler',
        '.save-preference-btn-handler',
      ].forEach((selector) => document?.body?.querySelector(selector)?.addEventListener('click', iframeRefresh));
    });
  }

  const handleIframeHeight = iframeHeight ? { height: `${iframeHeight}px` } : {};

  const handleMinIframeHeight = shouldAddMinHeigt ? { minHeight: '100vh' } : {};

  return (
    <iframe
      title='Embedded Content'
      key={state.iframe_key}
      slot={slot}
      srcDoc={blob ? getParsedBlob(blob, iframeHeight, shouldAddCssInjection, shouldAutomaticResize) : undefined}
      src={url || undefined}
      style={{
        border: 'none',
        width: '100%',
        ...handleIframeHeight,
        ...handleMinIframeHeight,
      }}
    />
  );
};
